import {defaultSearchParams, TransactionSearchParams} from '@/types/transaction/TransactionSearchParams'
import {get, httpDelete, post} from '@/services/infrastructure/api.service'
import {App as VueApp, ref} from 'vue'
import {getTransactionByUuidKey} from '@/types/transaction/Transaction.injects'
import {Transaction} from '@/types/transaction/Transaction'
import {useEmitter} from '@/services/infrastructure/emitter'
import {DELETE_TRANSACTION, TRANSACTION_CREATED, TRANSACTION_UPDATED} from '@/types/transaction/TransactionEvent' // TODO: Move to TransactionCreator service
import {ORG_SELECTED} from '@/types/events'
import {TransactionQueryParams} from '@/types/transaction/TransactionQueryParams'
import {IGetRowsParams} from 'ag-grid-community'
import {toQueryRequest} from '@/query/AgGridAdapter'
import {QueryRequest} from '@/query/QueryRequest'
import {notifyError} from '@/services/infrastructure/notification.service'
import {TransactionCommandCreate} from '@/transaction/types/TransactionCommands'
import {TransactionLine} from '@/types/transaction/TransactionLine'
import {emptyPage, Page} from '@/types/Page'
import {getFeatureValue} from '@/feature-toggles/feature-toggler.service'

const TRANSACTIONS_PATH = '/transactions'

const TRANSACTION_LINE_PATH = '/-/transaction-lines'

export type Offsettable = {
    startRow: number,
    endRow: number
}

const findTransactionsByIGETRowsParams = async (params: IGetRowsParams & { search?: string }, headers: any = {}): Promise<Transaction[]> => {
    return post(TRANSACTIONS_PATH + '/v2', toQueryRequest(params as any, 'fullText'), headers)
}

const searchTransactionLines = async (queryParams: TransactionSearchParams): Promise<TransactionLine[]> => {
    return await get(TRANSACTIONS_PATH + TRANSACTION_LINE_PATH, {
        size: 10,
        ...queryParams
    }) ?? []
}

const getTransactionLinesByIGetRowsParams = async (params: IGetRowsParams & { search?: string }, headers: any = {}): Promise<TransactionLine[]> => {
    return await post(TRANSACTIONS_PATH + TRANSACTION_LINE_PATH + '/v2', toQueryRequest(params as any, 'fullText'), headers) ?? []
}

const getTransactionLines = async (request: QueryRequest, headers: any = {}): Promise<TransactionLine[]> => {
    return await post(TRANSACTIONS_PATH + TRANSACTION_LINE_PATH + '/v2', request, headers) ?? []
}

const searchTransactionLinesWithOffset = async (queryParams: TransactionSearchParams, offsetable: Offsettable): Promise<TransactionLine[]> => {
    return await get(TRANSACTIONS_PATH + TRANSACTION_LINE_PATH, {
        ...queryParams,
        ...offsetable
    }) ?? []
}

const getTransactionsByInvoiceUuid = async (searchParams: TransactionQueryParams): Promise<Array<Transaction> | undefined> => {
    return get<Page<Transaction>>(TRANSACTIONS_PATH, {
        ...searchParams
    }).then(p => p?.content ?? [])
}

const countTransactions = async (queryParams: TransactionQueryParams): Promise<number | undefined> => {
    return get(TRANSACTIONS_PATH + '/count', queryParams)
}

const getTransactionByUuid = async (uuid: string): Promise<Transaction | undefined> => {
    try {
        return await get(`${TRANSACTIONS_PATH}/${uuid}`, {})
    } catch (e: any) {
        // TODO something any api service get should handle?
        if (e?.response?.status === 404) return undefined
        throw e
    }
}

const {onEvent, cleanUp} = useEmitter()

const transactions = ref<Transaction[]>([])
const searchParams = ref(defaultSearchParams())

const totalPages = ref(0)
const loading = ref(true)


const setPage = async (pageNum: number) => {
    searchParams.value.page = pageNum

    const page = await searchTransactions()
    transactions.value = page.content || []
    totalPages.value = page.totalPages
}

// TODO: Make a page type, and specify return page type, and create a helper method e.g. defaultPage()
const searchTransactions = async (size: number = 100): Promise<Page<Transaction>> => {
    loading.value = true
    try {
        return await get<Page<Transaction>>(TRANSACTIONS_PATH, {
            size: size,
            ...searchParams.value
        }) ?? emptyPage
    } catch (e: any) {
        notifyError({title: 'Feilet', description: {descriptionMessage: 'Customize Toolbar…', error: e}})
        return emptyPage
    } finally {
        loading.value = false
    }
}

const findLatestTransactions = async (): Promise<Transaction[]> => {
    loading.value = true
    try {
        return await get(TRANSACTIONS_PATH + '/latest') ?? []
    } catch (e: any) {
        notifyError({title: 'Feilet', description: {descriptionMessage: 'Error in findLatestTransactions', error: e}})
        return []
    } finally {
        loading.value = false
    }
}

const postTransaction = async(command: TransactionCommandCreate): Promise<Transaction> => {
    return await post(TRANSACTIONS_PATH, command, {
        'X-DagEnabled': getFeatureValue('DagEnabled', false)
    })
}

const clearTransactionList = () => {
    transactions.value = []
}

const initialFetch = async () => {
    searchParams.value.page = 0
    const page = await searchTransactions()

    transactions.value = page.content || []
    totalPages.value = page.totalPages
}

const deleteTransaction = async (uuid: string) => {
    return await httpDelete(`${TRANSACTIONS_PATH}/${uuid}`)
}

const bindEventHandlers = () => {
    onEvent(ORG_SELECTED, () => {
        clearTransactionList()
        initialFetch().then()
    })

    onEvent(TRANSACTION_CREATED, initialFetch)
    onEvent(TRANSACTION_UPDATED, initialFetch)

    onEvent(DELETE_TRANSACTION, initialFetch)
}

const onCleanup = () => {
    cleanUp()
}

const plugin = {
    install(app: VueApp) {
        app
            .provide(getTransactionByUuidKey, getTransactionByUuid)
    }
}

export {
    transactions,
    searchParams,
    totalPages,
    setPage,
    searchTransactions,
    searchTransactionLinesWithOffset,
    findLatestTransactions,
    postTransaction,
    countTransactions,
    bindEventHandlers,
    onCleanup,
    loading,
    searchTransactionLines,
    getTransactionByUuid,
    getTransactionsByInvoiceUuid,
    plugin,
    getTransactionLinesByIGetRowsParams,
    getTransactionLines,
    findTransactionsByIGETRowsParams,
    deleteTransaction
}

export const checkDuplicateInvoiceNo = async (invoiceNo: string, subAccountUuid: string): Promise<boolean> => {
    return await get<boolean>('/invoice/-/duplicate-invoice-check', {invoiceNo, subAccountUuid}) ?? false
}